"use client"

import { useEffect } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { useStore } from '@/lib/store'
import { createClient } from '@/lib/supabase/client'

/**
 * 106 — a change on one device reaches the shop's other devices.
 *
 * The owner, 2026-09-23: MINIMAX open in a browser, in the installed app and
 * on a phone; a sale on one appeared on the others only after Ctrl+R. The
 * database now broadcasts WHICH table changed (never the row) on the private
 * topic `org:<id>`; this listens, gathers what arrives for a moment, and asks
 * the store to re-read just those slices (`refreshLive`).
 *
 * - Gathered for 1.2 s: one sale sends two or three signals (sales,
 *   product_stock, maybe customers) — one re-read covers them.
 * - A hidden tab keeps the list and re-reads when it is shown again, so a
 *   phone in a pocket does not download every sale of the day.
 * - After the socket reconnects, EVERYTHING is re-read once: signals sent
 *   while it was down are gone for good.
 * - The till's price freeze is not bypassed: products may refresh under an
 *   open payment dialog, but the cart re-prices only when it closes (POS page,
 *   audit 2026-09-16).
 *
 * The 3-minute and on-focus refreshes stay: they are the floor if Realtime is
 * down, not the thing a shop should notice.
 */
const ALL_TABLES = [
  'sales', 'product_stock', 'products', 'purchases', 'shifts', 'customers',
  'customer_payments', 'supplier_payments', 'suppliers', 'expenses',
  'stock_transfers', 'promotions', 'recurring_expenses', 'cart_removals', 'price_changes',
  // 170 — a return to a supplier; 107 — the drawer (a return or a receipt writes it)
  'supplier_returns', 'cash_movements', 'prepaid_moves',
  // 1.65 (191) — a till's need, an order saved or received on another device
  'demand_records', 'supplier_orders', 'supplier_order_items',
  // 178 (1.58) — the owner's price groups (a till prices a grouped customer with them)
  'price_groups',
  // 1.71 (200) — the add-on rules (a coffee's cup and lid): a rule changed at noon reaches every till and phone
  'product_addons',
  // 1.61 (184) — the batches (a sale on another till drains one; the owner writes one off)
  'stock_lots',
  // 1.57 (177) — an owner's switch: refreshLive reads only the `feature.*` rows
  'settings',
  // 1.66 (192) — a café's halls, tables and bills (refreshLive reads them only where the tables screen opened)
  'dining_halls', 'dining_tables', 'table_orders', 'table_order_lines', 'table_order_batches',
  // 1.69 (197/198) — the parties at the tables and the guests' requests
  'table_visits', 'table_requests', 'table_request_lines',
  // 1.67 (193) — a waiter switched off, added or moved: his phone and the other waiters' feeds hear it
  'cashiers',
]

export function LiveSync() {
  const orgId = useStore((s) => s.currentOrg?.id ?? null)
  const hydrated = useStore((s) => s.isHydrated)

  useEffect(() => {
    if (!orgId || !hydrated) return
    const supabase = createClient()
    let channel: RealtimeChannel | null = null
    let cancelled = false
    let pending = new Set<string>()
    let timer: number | undefined
    let subscribedOnce = false

    const flush = () => {
      timer = undefined
      if (document.visibilityState !== 'visible' || pending.size === 0) return
      const tables = [...pending]
      pending = new Set()
      void useStore.getState().refreshLive(tables)
    }
    const note = (table: string) => {
      pending.add(table)
      if (timer === undefined) timer = window.setTimeout(flush, 1200)
    }
    const onVisible = () => { if (document.visibilityState === 'visible') flush() }

    void (async () => {
      // A private channel is authorised by the user's JWT (RLS on
      // realtime.messages, migration 106) — hand it over before joining.
      await supabase.realtime.setAuth()
      if (cancelled) return
      channel = supabase
        .channel(`org:${orgId}`, { config: { private: true } })
        .on('broadcast', { event: 'changed' }, ({ payload }: { payload?: unknown }) => {
          const t = (payload as { t?: unknown } | undefined)?.t
          if (typeof t === 'string') note(t)
        })
        .subscribe((status: string) => {
          if (status !== 'SUBSCRIBED') return
          // back after a drop: everything; the FIRST time: the sales — a sale
          // committed between the boot's read and this subscription (1.24)
          if (subscribedOnce) ALL_TABLES.forEach(note)
          else { note('sales'); note('table_orders') }   // 1.66: a bill changed between the first read and now
          subscribedOnce = true
        })
    })()
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      if (timer !== undefined) window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
      if (channel) void supabase.removeChannel(channel)
    }
  }, [orgId, hydrated])

  return null
}
